import React, { useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const now = new Date();

export default function Reports() {
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/monthly-report', { params: { month, year } });
      setReport(res.data);
    } finally { setLoading(false); }
  };

  const fmt = n => `PKR ${(n || 0).toLocaleString()}`;

  return (
    <Layout title="Monthly Report">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📈 Monthly Report</h3>
        </div>
        <div className="filters-bar">
          <select value={month} onChange={e => setMonth(e.target.value)}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(e.target.value)}>
            {[2023, 2024, 2025, 2026, 2027].map(y => <option key={y}>{y}</option>)}
          </select>
          <button className="btn btn-primary" onClick={loadReport} disabled={loading}>
            {loading ? 'Loading...' : '🔍 Generate Report'}
          </button>
        </div>
      </div>

      {report && (
        <>
          {/* Summary Cards */}
          <div className="stats-grid">
            <div className="stat-card" style={{ background: '#f0fff4' }}>
              <div className="stat-icon" style={{ background: '#38a169', color: '#fff' }}>💰</div>
              <div className="stat-info">
                <h3 style={{ fontSize: 16 }}>{fmt(report.summary.income)}</h3>
                <p>Fee Income</p>
              </div>
            </div>
            <div className="stat-card" style={{ background: '#fff5f5' }}>
              <div className="stat-icon" style={{ background: '#e53e3e', color: '#fff' }}>💸</div>
              <div className="stat-info">
                <h3 style={{ fontSize: 16 }}>{fmt(report.summary.expenses)}</h3>
                <p>Total Expenses</p>
              </div>
            </div>
            <div className="stat-card" style={{ background: '#fffaf0' }}>
              <div className="stat-icon" style={{ background: '#d69e2e', color: '#fff' }}>⚠️</div>
              <div className="stat-info">
                <h3 style={{ fontSize: 16 }}>{fmt(report.summary.pendingFees)}</h3>
                <p>Pending Fees</p>
              </div>
            </div>
            <div className="stat-card" style={{ background: report.summary.netBalance >= 0 ? '#f0fff4' : '#fff5f5' }}>
              <div className="stat-icon" style={{ background: report.summary.netBalance >= 0 ? '#38a169' : '#e53e3e', color: '#fff' }}>📊</div>
              <div className="stat-info">
                <h3 style={{ fontSize: 16, color: report.summary.netBalance >= 0 ? '#276749' : '#c53030' }}>
                  {fmt(report.summary.netBalance)}
                </h3>
                <p>Net Balance</p>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Expense Breakdown */}
            <div className="card">
              <div className="card-header"><h3 className="card-title">💸 Expense Breakdown</h3></div>
              {[
                { label: 'Bills', value: report.summary.billsTotal, icon: '⚡' },
                { label: 'Maintenance', value: report.summary.maintenanceTotal, icon: '🔧' },
                { label: 'Staff Salaries', value: report.summary.salariesTotal, icon: '👥' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f4f8' }}>
                  <span style={{ fontSize: 13 }}>{item.icon} {item.label}</span>
                  <strong style={{ fontSize: 13 }}>{fmt(item.value)}</strong>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', marginTop: 4 }}>
                <strong>Total</strong>
                <strong style={{ color: '#c53030' }}>{fmt(report.summary.expenses)}</strong>
              </div>
            </div>

            {/* Fee Status Summary */}
            <div className="card">
              <div className="card-header"><h3 className="card-title">💰 Fee Status</h3></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f4f8' }}>
                <span style={{ fontSize: 13 }}>✅ Collected</span>
                <strong style={{ fontSize: 13, color: '#276749' }}>{fmt(report.summary.income)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f4f8' }}>
                <span style={{ fontSize: 13 }}>⚠️ Pending</span>
                <strong style={{ fontSize: 13, color: '#d69e2e' }}>{fmt(report.summary.pendingFees)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
                <span style={{ fontSize: 13 }}>📋 Total Records</span>
                <strong style={{ fontSize: 13 }}>{report.fees.length}</strong>
              </div>
            </div>
          </div>

          {/* Fee Details Table */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">🎓 Student Fee Details — {MONTHS[month - 1]} {year}</h3></div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Student</th><th>Room</th><th>Amount</th><th>Fine</th><th>Status</th><th>Paid Date</th></tr>
                </thead>
                <tbody>
                  {report.fees.length === 0 ? (
                    <tr><td colSpan={6} className="text-center" style={{ padding: 20, color: '#718096' }}>No fee records</td></tr>
                  ) : report.fees.map(f => (
                    <tr key={f._id}>
                      <td>{f.student?.name || 'N/A'}</td>
                      <td><span className="badge badge-info">{f.student?.room}</span></td>
                      <td>PKR {f.amount.toLocaleString()}</td>
                      <td>{f.fine > 0 ? `PKR ${f.fine.toLocaleString()}` : '-'}</td>
                      <td><span className={`badge ${f.status === 'Paid' ? 'badge-success' : 'badge-danger'}`}>{f.status}</span></td>
                      <td style={{ fontSize: 12 }}>{f.paidDate ? f.paidDate.split('T')[0] : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bills Table */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">⚡ Bills — {MONTHS[month - 1]} {year}</h3></div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Type</th><th>Amount</th><th>Status</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {report.bills.length === 0 ? (
                    <tr><td colSpan={4} className="text-center" style={{ padding: 20, color: '#718096' }}>No bills</td></tr>
                  ) : report.bills.map(b => (
                    <tr key={b._id}>
                      <td><span className={`badge ${b.type === 'Electricity' ? 'badge-warning' : 'badge-info'}`}>{b.type === 'Electricity' ? '⚡' : '🔥'} {b.type}</span></td>
                      <td>PKR {b.amount.toLocaleString()}</td>
                      <td><span className={`badge ${b.status === 'Paid' ? 'badge-success' : 'badge-danger'}`}>{b.status}</span></td>
                      <td style={{ fontSize: 12 }}>{b.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Maintenance Table */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">🔧 Maintenance — {MONTHS[month - 1]} {year}</h3></div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Issue</th><th>Cost</th><th>Status</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {report.maintenance.length === 0 ? (
                    <tr><td colSpan={4} className="text-center" style={{ padding: 20, color: '#718096' }}>No maintenance records</td></tr>
                  ) : report.maintenance.map(m => (
                    <tr key={m._id}>
                      <td>{m.issue}</td>
                      <td>PKR {(m.cost || 0).toLocaleString()}</td>
                      <td><span className={`badge ${m.status === 'Completed' ? 'badge-success' : m.status === 'In Progress' ? 'badge-warning' : 'badge-danger'}`}>{m.status}</span></td>
                      <td style={{ fontSize: 12 }}>{m.date?.split('T')[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Salary Table */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">👥 Staff Salaries — {MONTHS[month - 1]} {year}</h3></div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Staff</th><th>Role</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {report.salaries.length === 0 ? (
                    <tr><td colSpan={4} className="text-center" style={{ padding: 20, color: '#718096' }}>No salary records</td></tr>
                  ) : report.salaries.map(s => (
                    <tr key={s._id}>
                      <td>{s.staff?.name}</td>
                      <td><span className={`badge ${s.staff?.role === 'Security Guard' ? 'badge-warning' : 'badge-info'}`}>{s.staff?.role}</span></td>
                      <td>PKR {s.amount.toLocaleString()}</td>
                      <td><span className={`badge ${s.status === 'Paid' ? 'badge-success' : 'badge-danger'}`}>{s.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
